import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import type { AnyTutorBookingSummaryCountsResponse, TutorBookingFilter } from '../../models/Tutor';
import { getTutorBookingFilterCount } from '../../utils/tutorBookingFilters';

export interface TutorBookingFilterTabsProps {
  filters: TutorBookingFilter[];
  activeFilter: TutorBookingFilter;
  summary: AnyTutorBookingSummaryCountsResponse | null;
  onChange: (filter: TutorBookingFilter) => void;
  /** Optional display labels when a role needs a custom tab title. */
  labelOverrides?: Partial<Record<TutorBookingFilter, string>>;
  /** Optional control aligned to the right of the tab row (e.g. student search). */
  trailing?: React.ReactNode;
}

const FILTER_LABEL_DEFAULTS: Record<TutorBookingFilter, string> = {
  upcoming: 'Upcoming',
  past: 'Past',
  needs_tutor_action: 'Needs action',
  needs_user_action: 'Needs action',
  pending: 'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  all: 'All',
};

const TutorBookingFilterTabs: React.FC<TutorBookingFilterTabsProps> = ({
  filters,
  activeFilter,
  summary,
  onChange,
  labelOverrides,
  trailing,
}) => {
  const { t } = useTranslation();
  const activeIndex = Math.max(0, filters.indexOf(activeFilter));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 1.5,
        rowGap: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Tabs
        value={activeIndex}
        onChange={(_, index) => onChange(filters[index])}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          '& .MuiTab-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
        }}
      >
        {filters.map((filter) => {
          const count = getTutorBookingFilterCount(summary, filter);
          const label =
            labelOverrides?.[filter] ??
            t(`tutorBookingFilters.${filter}`, FILTER_LABEL_DEFAULTS[filter]);
          return <Tab key={filter} label={`${label} (${count})`} />;
        })}
      </Tabs>
      {trailing ? (
        <Box
          sx={{
            flex: '0 0 auto',
            ml: 'auto',
            pb: 0.5,
            minWidth: { xs: '100%', sm: 'auto' },
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {trailing}
        </Box>
      ) : null}
    </Box>
  );
};

export default TutorBookingFilterTabs;
